import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { differenceInDays, format } from "date-fns";
import { RefreshCw, TrendingUp, AlertTriangle, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

interface RenewalWithDeal {
  id: string;
  deal_id: string;
  original_end_date: string;
  renewal_status: string;
  renewal_probability: number | null;
  next_reminder_at: string | null;
  deals: {
    id: string;
    title: string;
    brand_name: string;
    amount: number;
    athletes: {
      name: string;
    };
  };
}

export default function Renewals() {
  const [activeTab, setActiveTab] = useState("action_needed");

  const { data: renewals, isLoading } = useQuery({
    queryKey: ["all-renewals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contract_renewals" as any)
        .select(`
          *,
          deals:deal_id (
            id,
            title,
            brand_name,
            amount,
            athletes:athlete_id (
              name
            )
          )
        `)
        .order("original_end_date", { ascending: true });

      if (error) throw error;
      return data as unknown as RenewalWithDeal[];
    },
  });

  const categorizeRenewals = () => {
    if (!renewals) return { upcoming: [], actionNeeded: [], urgent: [], inProgress: [], completed: [] };

    const now = new Date();
    return renewals.reduce(
      (acc, renewal) => {
        const daysUntil = differenceInDays(new Date(renewal.original_end_date), now);

        if (renewal.renewal_status === "renewed" || renewal.renewal_status === "declined") {
          acc.completed.push(renewal);
        } else if (renewal.renewal_status === "in_progress") {
          acc.inProgress.push(renewal);
        } else if (daysUntil < 0) {
          acc.urgent.push(renewal);
        } else if (daysUntil <= 30) {
          acc.urgent.push(renewal);
        } else if (daysUntil <= 90) {
          acc.actionNeeded.push(renewal);
        } else {
          acc.upcoming.push(renewal);
        }

        return acc;
      },
      { upcoming: [] as RenewalWithDeal[], actionNeeded: [] as RenewalWithDeal[], urgent: [] as RenewalWithDeal[], inProgress: [] as RenewalWithDeal[], completed: [] as RenewalWithDeal[] }
    );
  };

  const categorized = categorizeRenewals();

  const RenewalCard = ({ renewal }: { renewal: RenewalWithDeal }) => {
    const daysUntil = differenceInDays(new Date(renewal.original_end_date), new Date());
    const isExpired = daysUntil < 0;

    return (
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <Link to={`/deals/${renewal.deals.id}`} className="font-medium hover:underline">
                  {renewal.deals.title}
                </Link>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{renewal.deals.brand_name}</span>
                  <span>•</span>
                  <span>{renewal.deals.athletes?.name}</span>
                </div>
              </div>
              <Badge variant={isExpired ? "destructive" : "outline"}>
                {isExpired ? "Expired" : `${daysUntil} days`}
              </Badge>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Expiration</span>
              <span className="font-medium">{format(new Date(renewal.original_end_date), "MMM d, yyyy")}</span>
            </div>

            {renewal.renewal_probability !== null && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Probability</span>
                <Badge
                  variant={
                    renewal.renewal_probability >= 75
                      ? "default"
                      : renewal.renewal_probability >= 40
                      ? "secondary"
                      : "destructive"
                  }
                >
                  {renewal.renewal_probability}%
                </Badge>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button size="sm" variant="outline" asChild>
                <Link to={`/deals/${renewal.deals.id}`}>View Deal</Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">Loading renewals...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Contract Renewals</h1>
          <p className="text-muted-foreground">
            Manage upcoming contract renewals and track renewal status
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Urgent</CardTitle>
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{categorized.urgent.length}</div>
              <p className="text-xs text-muted-foreground">
                Expiring in 30 days or less
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Action Needed</CardTitle>
              <RefreshCw className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{categorized.actionNeeded.length}</div>
              <p className="text-xs text-muted-foreground">
                30-90 days out
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">In Progress</CardTitle>
              <TrendingUp className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{categorized.inProgress.length}</div>
              <p className="text-xs text-muted-foreground">
                Being negotiated
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <CheckCircle className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{categorized.completed.length}</div>
              <p className="text-xs text-muted-foreground">
                Renewed or declined
              </p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="urgent">
              Urgent ({categorized.urgent.length})
            </TabsTrigger>
            <TabsTrigger value="action_needed">
              Action Needed ({categorized.actionNeeded.length})
            </TabsTrigger>
            <TabsTrigger value="upcoming">
              Upcoming ({categorized.upcoming.length})
            </TabsTrigger>
            <TabsTrigger value="in_progress">
              In Progress ({categorized.inProgress.length})
            </TabsTrigger>
            <TabsTrigger value="completed">
              Completed ({categorized.completed.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="urgent" className="space-y-4">
            {categorized.urgent.map((renewal) => (
              <RenewalCard key={renewal.id} renewal={renewal} />
            ))}
            {categorized.urgent.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No urgent renewals
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="action_needed" className="space-y-4">
            {categorized.actionNeeded.map((renewal) => (
              <RenewalCard key={renewal.id} renewal={renewal} />
            ))}
            {categorized.actionNeeded.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No renewals need action
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="upcoming" className="space-y-4">
            {categorized.upcoming.map((renewal) => (
              <RenewalCard key={renewal.id} renewal={renewal} />
            ))}
            {categorized.upcoming.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No upcoming renewals
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="in_progress" className="space-y-4">
            {categorized.inProgress.map((renewal) => (
              <RenewalCard key={renewal.id} renewal={renewal} />
            ))}
            {categorized.inProgress.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No renewals in progress
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="completed" className="space-y-4">
            {categorized.completed.map((renewal) => (
              <RenewalCard key={renewal.id} renewal={renewal} />
            ))}
            {categorized.completed.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No completed renewals
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
